<template>
  <div id="app">
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p>Loading OilBuddy...</p>
      </div>
    </div>
    
    <div v-else-if="hasError" class="error-screen">
      <div class="error-content">
        <h2>Something went wrong</h2>
        <p>{{ errorMessage }}</p>
        <button @click="reloadApp" class="reload-button">Reload Application</button>
      </div>
    </div>
    
    <router-view v-else />
    
    <!-- AI Assistant - Always available -->
    <!-- <OilBuddyAssistant /> -->
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminStore } from '@/stores/adminStore'
// import OilBuddyAssistant from '@/components/ai/OilBuddyAssistant.vue'

const router = useRouter()
const adminStore = useAdminStore()
const isLoading = ref(true)
const hasError = ref(false)
const errorMessage = ref('')

// Debug logging
console.log('App.vue is loading...')

// Error handling
const handleError = (error: any) => {
  console.error('App error:', error)
  hasError.value = true
  errorMessage.value = error.message || 'An unexpected error occurred'
  isLoading.value = false
}

// Reload application
const reloadApp = () => {
  window.location.reload()
}

// Initialize app
onMounted(async () => {
  try {
    // Don't clear admin data on page refresh - let the admin store handle its own initialization
    console.log('App initializing - preserving existing authentication state')
    
    // Initialize admin store from localStorage if admin data exists
    const adminToken = localStorage.getItem('adminToken')
    const adminUser = localStorage.getItem('adminUser')
    if (adminToken && adminUser) {
      console.log('Loading admin from storage on app startup')
      adminStore.loadAdminFromStorage()
    }
    
    // Log current authentication state for debugging
    const authToken = localStorage.getItem('auth_token')
    const userType = localStorage.getItem('user_type')
    console.log('Current auth state:', { hasToken: !!authToken, userType, hasAdminToken: !!adminToken })
    
    // Wait for router to be ready
    await router.isReady()
    console.log('Router is ready')
    
    // Simulate a small delay to ensure everything is loaded
    setTimeout(() => {
      isLoading.value = false
      console.log('App finished loading')
    }, 500)
    
  } catch (error) {
    handleError(error)
  }
})

// Log only. A rejected fetch or a page render error must not replace the whole app.
window.addEventListener('error', (event) => {
  console.error('App error:', event.error || event.message)
})

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled rejection:', event.reason)
})
</script>

<style>
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Loading Screen */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
  color: white;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #374151;
  border-top: 4px solid #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error Screen */
.error-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.error-content {
  text-align: center;
  color: white;
  max-width: 500px;
  padding: 40px;
}

.error-content h2 {
  color: #ef4444;
  margin-bottom: 20px;
}

.reload-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 20px;
  transition: all 0.3s ease;
}

.reload-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Smooth transitions */
* {
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}
</style>
